// Geteilter WebSocket-Hub fuer alle Frontend-Stores. Frueher hat
// jeder Store (security, tasks, actions) eine eigene Verbindung
// nach /ws gehalten -- 3 Sockets, 3 Reconnect-Loops, 3 JSON-Parser.
// Der Hub konsolidiert das auf eine einzige Verbindung mit channel-
// basiertem Routing. Backend (internal/ws/hub.go) unterstuetzt
// Multi-Channel pro Connection schon, wir nutzen das jetzt nur aus.
//
// API:
//   const off = subscribe("actions", (data) => { ... });
//   off(); // beim Teardown
//
// Lifecycle: erste subscribe() oeffnet die Verbindung. Bei Reconnect
// (Server-Restart, Netz-Hicker) werden alle aktiven Channel-Subs
// automatisch wieder reingespielt. Wenn der letzte Listener weg ist,
// bleibt die Verbindung erst mal offen -- wir machen sie nicht eilig
// zu, weil das Anlaufen kostet und die App eh single-tab laeuft.
// stopWsHub() ist der explizite Off-Switch (z.B. fuer Tests).

type Listener = (data: unknown) => void;

const _channels = new Map<string, Set<Listener>>();
let _ws: WebSocket | undefined;
let _reconnectTimer: ReturnType<typeof setTimeout> | undefined;
let _wantOpen = false;
// Zeitpunkt des letzten empfangenen Frames. Das Backend pingt alle 5s
// auf dem "ping"-Channel (Heartbeat-Store abonniert ihn), eine gesunde
// Leitung sieht also spaetestens alle ~5s etwas. Bleibt laenger als
// WS_STALE_MS alles aus, gilt die Verbindung als halb-offen (TCP haengt,
// kein sauberes onclose) und revalidateWsHub() erzwingt einen Reconnect.
let _lastMsgAt = 0;
const WS_STALE_MS = 12_000;
// Live-Verbindungsstatus: true zwischen onopen und onclose. Reaktiv,
// damit der Heartbeat einen Server-Stopp/Disconnect SOFORT sieht (das
// onclose feuert beim sauberen Close instant) statt erst per Timeout.
let _connected = $state(false);
// true, sobald die Leitung EINMAL offen war -- unterscheidet den ersten
// Connect (Boot, Stores laden via start* eh) vom Reconnect (Frames waren
// weg, Stores muessen frisch laden). Kein Timing-abhaengiges "erstes
// Event ueberspringen" beim Listener.
let _everOpened = false;
// Wie oft hintereinander ein Verbindungsversuch fehlgeschlagen ist.
// Treibt den Backoff (siehe wartezeit()); onopen setzt zurueck.
let _fehlversuche = 0;

/** true wenn die WS-Verbindung gerade offen ist. */
export function wsConnected(): boolean {
  return _connected;
}

function wsUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/ws`;
}

function sendSubscribe(ws: WebSocket, channel: string): void {
  try {
    ws.send(JSON.stringify({ type: "subscribe", channel }));
  } catch {
    // Wenn der Send fehlschlaegt, ist die Verbindung gleich tot --
    // onclose macht den Reconnect.
  }
}

// Fokus fürs Backend (ws.Hub.Fokus): solange ein Homelab-Fenster vorn ist,
// lässt der Server Pushs weg, die man hier ohnehin sieht. Ereignisgetrieben
// wie in bg-settings.ts; jede Minute erneuert, weil der Server einen Fokus
// nach 150 s vergisst (eingeschlafener Rechner, Leitung noch nicht tot).
let _fokus = false;
let _fokusBeobachtet = false;

function meldeFokus(): void {
  const ws = _ws;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  try {
    ws.send(JSON.stringify({ type: "fokus", fokus: _fokus }));
  } catch {
    // onclose macht den Reconnect, onopen meldet neu.
  }
}

function beobachteFokus(): void {
  if (_fokusBeobachtet) return;
  _fokusBeobachtet = true;
  const setzen = (f: boolean) => {
    if (f === _fokus) return;
    _fokus = f;
    meldeFokus();
  };
  window.addEventListener("blur", () => setzen(false));
  window.addEventListener("focus", () => setzen(true));
  document.addEventListener("visibilitychange", () => setzen(!document.hidden && document.hasFocus()));
  _fokus = !document.hidden && document.hasFocus();
  setInterval(() => {
    if (_fokus) meldeFokus();
  }, 60_000);
}

function connect(): void {
  if (_ws && _ws.readyState !== WebSocket.CLOSED) return;
  beobachteFokus();
  const ws = new WebSocket(wsUrl());
  _ws = ws;

  ws.onopen = () => {
    _connected = true;
    _fehlversuche = 0;
    // Frisch offene Leitung gilt als lebendig, bis der Stale-Timeout
    // (s.o.) ohne Frames ablaeuft.
    _lastMsgAt = Date.now();
    // Alle bekannten Channels neu abonnieren -- nach Reconnect ist das
    // der einzige Weg, weil der Server die Subs nicht persistiert.
    for (const channel of _channels.keys()) {
      sendSubscribe(ws, channel);
    }
    if (_fokus) meldeFokus();
    // Deploy-Signal: ein Restart trennt den Socket, der Reconnect feuert
    // dieses Event -> die PWA-Versionspruefung laedt bei neuem Build nach.
    // Feuert auch beim ersten Connect (harmlos: gleicher Build -> kein Reload).
    window.dispatchEvent(new CustomEvent("v2:ws-open"));
    // Reconnect (NICHT der erste Connect): waehrend die Leitung weg war,
    // wurden WS-Frames verpasst -- Recovery-Events, geleerte Alerts. Ohne
    // ein Refetch haengen Karten auf dem alten Fehlerzustand (Geister-Alert
    // nach WAN-Ausfall). visibilitychange/online decken das nicht ab: der
    // Tab bleibt sichtbar, das Client-WLAN bleibt verbunden.
    if (_everOpened) {
      window.dispatchEvent(new CustomEvent("v2:ws-reconnect"));
    }
    _everOpened = true;
  };

  ws.onmessage = (e) => {
    // Jeder Frame (auch der 5s-Ping) ist ein Lebenszeichen.
    _lastMsgAt = Date.now();
    let msg: unknown;
    try {
      msg = JSON.parse(e.data);
    } catch {
      return;
    }
    if (
      typeof msg !== "object" ||
      msg === null ||
      (msg as { type?: unknown }).type !== "message"
    ) {
      return;
    }
    const channel = (msg as { channel?: unknown }).channel;
    if (typeof channel !== "string") return;
    const subs = _channels.get(channel);
    if (!subs || subs.size === 0) return;
    const data = (msg as { data?: unknown }).data;
    for (const cb of subs) {
      try {
        cb(data);
      } catch {
        // Listener-Fehler darf andere Listener nicht killen.
      }
    }
  };

  ws.onclose = () => {
    _connected = false;
    _ws = undefined;
    if (_wantOpen) {
      _fehlversuche++;
      _reconnectTimer = setTimeout(connect, wartezeit(_fehlversuche));
    }
  };

  ws.onerror = () => {
    // Browser feuert onclose direkt danach -- dort wird reconnected.
  };
}

// wartezeit: wie lange bis zum naechsten Verbindungsversuch.
//
// Vorher standen hier fest 2000 ms — also alle zwei Sekunden ein neuer
// Handshake, unbegrenzt, solange die Leitung nicht zustande kommt. Auf
// einem Laptop heisst das: das Funkmodul kommt nie zur Ruhe. Safari
// misst genau so etwas und stoppt die Seite dann wegen „hohem
// Energiebedarf" (Tim, 01.09.2026).
//
// Jetzt: 1 s, 2 s, 4 s … bis 30 s, mit etwas Streuung. Der erste
// Versuch ist damit sogar SCHNELLER als vorher — was zaehlt, ist der
// Dauerfall. Die Streuung verhindert, dass nach einem Deploy alle
// offenen Tabs im Gleichschritt anklopfen.
//
// Ein echter Verbindungsabbruch (Deploy, Netzwechsel) faellt nicht
// darunter: dort greift forceReconnect(), das sofort neu aufbaut.
const WS_WARTE_MAX_MS = 30_000;

function wartezeit(fehlversuche: number): number {
  const roh = Math.min(1000 * 2 ** (fehlversuche - 1), WS_WARTE_MAX_MS);
  return roh + Math.random() * Math.min(roh * 0.25, 3000);
}

// Bestehende (vermutlich tote) Verbindung hart abklemmen und sofort neu
// aufbauen. Die Handler der alten Socket werden genullt, damit ihr
// spaetes onclose nicht noch einen zweiten Reconnect schedult.
function forceReconnect(): void {
  // Bewusste Geste ("jetzt bitte sofort") -- der Backoff faengt von
  // vorn an, sonst wartet ein Tab nach dem Aufwachen bis zu 30 s.
  _fehlversuche = 0;
  if (_reconnectTimer) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = undefined;
  }
  if (_ws) {
    const old = _ws;
    _ws = undefined;
    old.onopen = null;
    old.onmessage = null;
    old.onclose = null;
    old.onerror = null;
    try {
      old.close();
    } catch {
      /* ignore */
    }
  }
  _connected = false;
  connect();
}

// Wache für den SICHTBAREN Tab. revalidateWsHub() lief nur bei
// visibilitychange und online -- ein Wandtablet oder ein Schreibtisch-Tab,
// der vorn bleibt, während der Server hart neu startet oder sein Netz kurz
// weg ist, bekam kein onclose, die Leitung blieb OPEN und still, und alle
// WS-getriebenen Karten froren ein, bis jemand den Tab wechselte (Befund
// KI-Prüfung 21.10.2026; im Claude-Chat dieselbe Wache in chat.svelte.ts).
//
// Die Wache greift NUR die halb offene Leitung an (OPEN, aber kein Frame
// seit WS_STALE_MS). Ist die Leitung zu, wartet schon der Backoff-Timer aus
// onclose -- ein forceReconnect() hier setzte ihn auf null zurück und
// klopfte bei totem Server alle 5 s an. Im Hintergrund schweigt sie: dort
// entscheidet beim Zurückkommen visibilitychange.
const WACHE_MS = 5_000;
let _wache: ReturnType<typeof setInterval> | undefined;

function wache(): void {
  if (!_wantOpen || document.visibilityState !== "visible") return;
  const ws = _ws;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  // Maßstab ist der 5-s-Puls auf "ping" (Heartbeat-Store). Ohne Abo kommt
  // auf einer gesunden, ruhigen Leitung womöglich minutenlang nichts.
  if (!_channels.has("ping")) return;
  if (Date.now() - _lastMsgAt < WS_STALE_MS) return;
  forceReconnect();
}

function starteWache(): void {
  if (_wache) return;
  _wache = setInterval(wache, WACHE_MS);
}

/**
 * Verbindung pruefen und bei Bedarf neu aufbauen. Aufgerufen beim
 * Tab-Refocus / Netz-zurueck: ein lange im Hintergrund liegender Tab kann
 * eine halb-offene Leitung haben (readyState OPEN, aber keine Frames mehr)
 * -- die wuerde sonst nie reconnecten, weil kein onclose feuert. Billig:
 * tut nur etwas, wenn die Leitung tatsaechlich tot ist.
 */
export function revalidateWsHub(): void {
  if (!_wantOpen) return;
  // Verbindungsaufbau laeuft schon -- in Ruhe lassen.
  if (_ws && _ws.readyState === WebSocket.CONNECTING) return;
  const alive =
    _ws &&
    _ws.readyState === WebSocket.OPEN &&
    Date.now() - _lastMsgAt < WS_STALE_MS;
  if (alive) return;
  forceReconnect();
}

/** Subscribe to a channel. Returns an unsubscribe function. */
export function subscribe(channel: string, cb: Listener): () => void {
  let set = _channels.get(channel);
  const isNewChannel = !set;
  if (!set) {
    set = new Set();
    _channels.set(channel, set);
  }
  set.add(cb);

  if (!_wantOpen) {
    _wantOpen = true;
    starteWache();
    connect();
  } else if (isNewChannel && _ws && _ws.readyState === WebSocket.OPEN) {
    // Verbindung steht schon, neuen Channel sofort nachregistrieren.
    sendSubscribe(_ws, channel);
  }

  return () => {
    const s = _channels.get(channel);
    if (!s) return;
    s.delete(cb);
    // Letzter Listener weg -> Channel auch serverseitig abmelden.
    // Wichtig fuer Praesenz-Kanaele (z.B. "openwrt:live"), bei denen
    // das blosse Abonnement etwas bedeutet -- der Hub feuert dann
    // sein onInactive. Fuer Daten-Kanaele kostet das nur einen
    // Re-Subscribe-Roundtrip, falls spaeter wieder jemand kommt.
    if (s.size === 0) {
      _channels.delete(channel);
      if (_ws && _ws.readyState === WebSocket.OPEN) {
        try {
          _ws.send(JSON.stringify({ type: "unsubscribe", channel }));
        } catch {
          // Verbindung stirbt eh gleich -- unregister raeumt serverseitig auf.
        }
      }
    }
  };
}

/** Tear the hub down explicitly (tests, full sign-out). */
export function stopWsHub(): void {
  _wantOpen = false;
  _connected = false;
  if (_wache) {
    clearInterval(_wache);
    _wache = undefined;
  }
  if (_reconnectTimer) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = undefined;
  }
  if (_ws) {
    _ws.close();
    _ws = undefined;
  }
  _channels.clear();
}
